import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { MainLayout } from './components/layout/main-layout'
import { clearTokens, getTokenPayload, isAuthenticated } from './lib/auth'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const dbsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/cloud/dbs',
  component: lazyRouteComponent(() => import('./pages/cloud/dbs'), 'DbsPage'),
})

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: MainLayout,
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { message: undefined },
      })
    }

    const payload = getTokenPayload()
    if (!payload || payload.realm !== 'ember_admin') {
      clearTokens()
      throw redirect({
        to: '/login',
        search: { message: 'Access denied — not an Ember admin account' },
      })
    }
  },
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('./pages/login'), 'LoginPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      message: (search['message'] as string) || undefined,
    }
  },
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
})

const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('./pages/dashboard'), 'DashboardPage'),
})

const partnersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/partners',
  component: lazyRouteComponent(() => import('./pages/partners'), 'PartnersPage'),
})

const partnerDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/partners/$partnerId',
  component: lazyRouteComponent(() => import('./pages/partners/detail'), 'PartnerDetailPage'),
})

const tenantsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tenants',
  component: lazyRouteComponent(() => import('./pages/tenants'), 'TenantsPage'),
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tenants/$tenantId',
  component: lazyRouteComponent(() => import('./pages/tenants/detail'), 'TenantDetailPage'),
})

const verticalsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/verticals',
  component: lazyRouteComponent(() => import('./pages/verticals'), 'VerticalsPage'),
})

const plansRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('./pages/plans'), 'PlansPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      verticalId: (search['verticalId'] as string) || undefined,
    } as { verticalId?: string }
  },
})

const addonsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/addons',
  component: lazyRouteComponent(() => import('./pages/addons'), 'AddonsPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      verticalId: (search['verticalId'] as string) || undefined,
    } as { verticalId?: string }
  },
})

const featuresRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/features',
  component: lazyRouteComponent(() => import('./pages/features'), 'FeaturesPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      verticalId: (search['verticalId'] as string) || undefined,
    } as { verticalId?: string }
  },
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/subscriptions',
  component: lazyRouteComponent(() => import('./pages/subscriptions'), 'SubscriptionsPage'),
})

const invoicesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/invoices',
  component: lazyRouteComponent(() => import('./pages/invoices'), 'InvoicesPage'),
})

const commissionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/commissions',
  component: lazyRouteComponent(() => import('./pages/commissions'), 'CommissionsPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      partnerId: (search['partnerId'] as string) || undefined,
      status: (search['status'] as string) || undefined,
    } as { partnerId?: string; status?: string }
  },
})

const usersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('./pages/users'), 'UsersPage'),
})

const rolesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/roles',
  component: lazyRouteComponent(() => import('./pages/roles'), 'RolesPage'),
})

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/settings'), 'SettingsPage'),
})

const growthRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/growth',
  component: lazyRouteComponent(() => import('./pages/growth'), 'GrowthPage'),
})

const supportInboxRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support/requests',
  component: lazyRouteComponent(() => import('./pages/support/inbox'), 'SupportInboxPage'),
  validateSearch: (search: Record<string, unknown>) => {
    const str = (key: string) => (search[key] as string) || undefined
    const page = Number(search['page'])
    return {
      vertical_id: str('vertical_id'),
      tenant_id: str('tenant_id'),
      type: str('type'),
      status: str('status'),
      priority: str('priority'),
      assigned_to: str('assigned_to'),
      q: str('q'),
      page: page > 1 ? page : undefined,
    } as Record<string, string | number | undefined>
  },
})

const supportDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support/requests/$requestId',
  component: lazyRouteComponent(() => import('./pages/support/detail'), 'SupportDetailPage'),
})

const supportDashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support/dashboard',
  component: lazyRouteComponent(() => import('./pages/support/dashboard'), 'SupportDashboardPage'),
})

const videoGeneratorRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tools/video-generator',
  component: lazyRouteComponent(
    () => import('./pages/tools/video-generator'),
    'VideoGeneratorPage',
  ),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    partnersRoute,
    partnerDetailRoute,
    tenantsRoute,
    tenantDetailRoute,
    verticalsRoute,
    plansRoute,
    addonsRoute,
    featuresRoute,
    subscriptionsRoute,
    invoicesRoute,
    commissionsRoute,
    usersRoute,
    rolesRoute,
    settingsRoute,
    dbsRoute,
    growthRoute,
    supportInboxRoute,
    supportDetailRoute,
    supportDashboardRoute,
    videoGeneratorRoute,
  ]),
])

// Every page is its own chunk (lazyRouteComponent above), so the first load only
// downloads the layout and the current page. 'intent' preloads a page's chunk when
// the user hovers or focuses a link to it, so navigating still feels instant.
export const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
